import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import { clearNewsArticleMeta, setNewsArticleMeta } from "@/lib/socialMeta";

type BranchRef = { name: string; slug: string } | null;
type BatchRef = {
  slug: string;
  custom_title: string | null;
  official_title: string | null;
  al_year: number | null;
} | null;
type AssociationRef = { name: string; slug: string } | null;

type NewsRow = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  slug: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  branch: BranchRef;
  batch: BatchRef;
  association: AssociationRef;
};

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function batchLabel(b: NonNullable<BatchRef>) {
  return b.custom_title || b.official_title || "Batch";
}

function ownerBadge(n: NewsRow): { label: string; href: string } {
  if (n.branch) return { label: n.branch.name, href: `/alumni/branches/${n.branch.slug}` };
  if (n.batch) return { label: batchLabel(n.batch), href: `/alumni/batches/${n.batch.slug}` };
  if (n.association) return { label: n.association.name, href: `/alumni/associations/${n.association.slug}` };
  return { label: "OBA", href: "/about" };
}

function isOba(n: NewsRow) {
  return !n.branch && !n.batch && !n.association;
}

export function NewsDetailPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<NewsRow | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    clearNewsArticleMeta();
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setRow(null);
    setLoading(true);
    setNotFound(false);
    void supabase
      .from("news")
      .select(
        `
        id,
        title,
        description,
        image_url,
        slug,
        published_at,
        created_at,
        updated_at,
        branch:branches(name, slug),
        batch:batches(slug, custom_title, official_title, al_year),
        association:associations(name, slug)
      `
      )
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setRow(null);
          setNotFound(true);
          setLoading(false);
          return;
        }
        const d = data as {
          id: string;
          title: string;
          description: string | null;
          image_url: string | null;
          slug: string | null;
          published_at: string | null;
          created_at: string | null;
          updated_at: string | null;
          branch: BranchRef | BranchRef[];
          batch: BatchRef | BatchRef[];
          association: AssociationRef | AssociationRef[];
        };
        const normalized: NewsRow = {
          id: d.id,
          title: d.title,
          description: d.description,
          image_url: d.image_url,
          slug: d.slug,
          published_at: d.published_at,
          created_at: d.created_at,
          updated_at: d.updated_at,
          branch: one(d.branch),
          batch: one(d.batch),
          association: one(d.association),
        };

        const cutoff = maxPublicBatchAlYear();
        const b = normalized.batch;
        if (b?.al_year != null && b.al_year > cutoff) {
          setRow(null);
          setNotFound(true);
          setLoading(false);
          return;
        }

        setRow(normalized);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (!row) return;
    const seg = row.slug ?? slug ?? "";
    const path = isOba(row) ? `/news/${seg}` : `/news/other/${seg}`;
    setNewsArticleMeta({
      title: row.title,
      description: row.description,
      imageUrl: row.image_url,
      canonicalPath: path,
    });
    return () => {
      clearNewsArticleMeta();
    };
  }, [row]);

  if (!slug) return null;

  if (!loading && notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-brand-blue/70">
        News item not found.
        <div className="mt-6">
          <Link to="/news" className="text-brand-maroon font-semibold">
            ← OBA News
          </Link>
          <span className="mx-3 text-brand-blue/30">|</span>
          <Link to="/news/other" className="text-brand-maroon font-semibold">
            Affiliated News →
          </Link>
        </div>
      </div>
    );
  }

  const badge = row ? ownerBadge(row) : null;
  const backHref = row && !isOba(row) ? "/news/other" : "/news";
  const backLabel = row && !isOba(row) ? "Affiliated News" : "OBA News";
  const detailDateLine = row ? formatNewsMonthYear(row.updated_at ?? row.created_at) : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-14">
      <Link to={backHref} className="text-sm font-semibold text-brand-maroon hover:underline">
        ← {backLabel}
      </Link>

      {loading ? (
        <p className="mt-8 text-brand-blue/60">Loading…</p>
      ) : row ? (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
            {badge ? (
              <Link
                to={badge.href}
                className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
              >
                {badge.label}
              </Link>
            ) : null}
            {detailDateLine ? (
              <time
                dateTime={row.updated_at ?? row.created_at ?? undefined}
                className="text-xs font-semibold uppercase tracking-wide text-brand-maroon/80"
              >
                {detailDateLine}
              </time>
            ) : null}
          </div>

          <h1 className="mt-4 font-display text-4xl font-bold text-brand-blue">
            {row.title}
          </h1>

          {row.image_url ? (
            <img
              src={row.image_url}
              alt=""
              className="mt-8 w-full max-h-[520px] rounded-2xl object-cover"
            />
          ) : null}

          {row.description ? (
            <div className="prose prose-blue mt-8 max-w-none">
              <p className="whitespace-pre-line text-brand-blue/90 leading-relaxed">
                {row.description}
              </p>
            </div>
          ) : (
            <p className="mt-8 text-brand-blue/70">No details provided.</p>
          )}
        </>
      ) : null}
    </div>
  );
}

